// ink-component
import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import StickyTableHeader from './virtualizer/StickyTableHeader';

import NewTableBody from './components/NewTableBody/NewTableBody';
import NewTableCell from './components/NewTableCell/NewTableCell';
import NewTableFoot from './components/NewTableFoot/NewTableFoot';
import NewTableHead from './components/NewTableHead/NewTableHead';
import NewTableHeadCell from './components/NewTableHeadCell/NewTableHeadCell';
import NewTableOrderableBody from './components/NewTableOrderableBody/NewTableOrderableBody';
import NewTableOrderableRow from './components/NewTableOrderableRow/NewTableOrderableRow';
import NewTablePin from './components/NewTablePin/NewTablePin';
import NewTableRibbon from './components/NewTableRibbon/NewTableRibbon';
import NewTableRow from './components/NewTableRow/NewTableRow';
import NewTableShimmer from './components/NewTableShimmer/NewTableShimmer';
import NewTableStripe from './components/NewTableStripe/NewTableStripe';
import NewTableTwiddle from './components/NewTableTwiddle/NewTableTwiddle';
import NewTableVirtualWrapper, {
  NewTableVirtualContext,
  NewTableVirtualizedElement,
} from './components/NewTableVirtualWrapper/NewTableVirtualWrapper';
import NewTableContext from './NewTableContext';

import { useCalculateFrozenCellSettings } from './hooks/freeze';
import { TwiddleOpenContextProvider } from './components/NewTableTwiddle/contexts';

export interface ScrollableWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
  height?: string;
}

export interface NewTableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  /** Defines the height of NewTable, in pixels. Do not set a height bigger than the sum of rows because it will render a blank space. Use with a CSS length unit. */
  height?: string;
  /** Changes the visual density of NewTable. Higher densities will have less padding and smaller font sizes. If you need to use CustomText, change the size of the font accordingly (13px for "high" and 12px for "higher") */
  density?: 'higher' | 'high' | 'normal' | 'low';
  /** Sets the gap from the top of the screen where the header should stick to. Usually 60px. */
  offsetStickyTop?: number;
}

const ScrollableWrapper = styled.div //
  .attrs(props => ({ style: { '--table-clientwidth': `${props.theme.clientWidth}px` } }))<ScrollableWrapperProps>`
  display: flex;
  max-height: ${({ theme }) => theme.height};

  overflow: auto;

  position: relative;
  width: 100%;
  font-variant-numeric: tabular-nums;
`;

const NewTableWrapper = styled.table<NewTableProps & NewTableVirtualizedElement>`
  display: ${({ virtual }) => (virtual ? 'grid' : 'table')};
  z-index: 0;
  height: 100%;
  min-width: 100%;
`;

/**
 * @group NewTable
 * @purpose Displays complex data as tabular structures
 * @categories table
 * @aka table
 * @HTMLType HTMLTableElement
 */
function NewTable({ style, className, height, density, offsetStickyTop, ...otherProps }: NewTableProps) {
  /* eslint-disable ssr-friendly/no-dom-globals-in-react-fc */
  const scrollableWrapperRef = React.useRef<HTMLDivElement>(null);
  const tableRef = React.useRef<HTMLTableElement>(null);
  const headCloneRef = React.useRef<HTMLTableElement>(null);
  const footCloneRef = React.useRef<HTMLTableElement>(null);
  /* eslint-enable ssr-friendly/no-dom-globals-in-react-fc */

  const virtual = React.useContext(NewTableVirtualContext);
  const frozenCellWidthCache = useCalculateFrozenCellSettings(tableRef);

  React.useLayoutEffect(() => {
    if (offsetStickyTop === undefined || height) {
      // This table scrolls in a container with a fixed height
      // and we don't need to set up sticky header/footer with
      // this class as position: sticky works in those cases.
      return;
    }

    if (!(tableRef.current && headCloneRef.current && footCloneRef.current)) {
      // Haven't fully mounted yet
      return;
    }

    const sticky = new StickyTableHeader({
      tableContainer: tableRef.current,
      headCloneContainer: headCloneRef.current,
      footCloneContainer: footCloneRef.current,
      offsetTop: offsetStickyTop,
    });

    return () => {
      sticky.destroy();
    };
  }, [height, virtual, offsetStickyTop]);

  if (height && virtual?.type === 'window') {
    throw new Error(`NewTable: The height prop is not supported when using useWindowTableVirtualizer.`);
  }

  if (height && offsetStickyTop !== undefined) {
    throw new Error(`NewTable: height and offsetStickyTop are mutually exclusive. Use one or the other.`);
  }

  const tableContextValue = React.useMemo(
    () => ({
      scrollableWrapperRef,
      frozenCellWidthCache,
    }),
    [scrollableWrapperRef, frozenCellWidthCache],
  );

  return (
    <NewTableContext.Provider value={tableContextValue}>
      <ThemeProvider
        theme={currentTheme => ({
          ...currentTheme,
          density,
          height,
          clientWidth: scrollableWrapperRef.current?.clientWidth,
          isTable: true,
        })}
      >
        <TwiddleOpenContextProvider>
          <ScrollableWrapper ref={scrollableWrapperRef}>
            <NewTableWrapper virtual={!!virtual} ref={tableRef} {...otherProps} />
          </ScrollableWrapper>
          {offsetStickyTop !== undefined && (
            <>
              <ScrollableWrapper className="head">
                <table ref={headCloneRef} />
              </ScrollableWrapper>
              <ScrollableWrapper className="foot">
                <table ref={footCloneRef} />
              </ScrollableWrapper>
            </>
          )}
        </TwiddleOpenContextProvider>
      </ThemeProvider>
    </NewTableContext.Provider>
  );
}

NewTable.Body = NewTableBody;
NewTable.Cell = NewTableCell;
NewTable.Foot = NewTableFoot;
NewTable.Head = NewTableHead;
NewTable.HeadCell = NewTableHeadCell;
NewTable.OrderableBody = NewTableOrderableBody;
NewTable.OrderableRow = NewTableOrderableRow;
NewTable.Pin = NewTablePin;
NewTable.Ribbon = NewTableRibbon;
NewTable.Row = NewTableRow;
NewTable.Shimmer = NewTableShimmer;
NewTable.Stripe = NewTableStripe;
NewTable.Twiddle = NewTableTwiddle;
NewTable.VirtualWrapper = NewTableVirtualWrapper;

export { NewTableBodyProps } from './components/NewTableBody/NewTableBody';
export { NewTableCellProps } from './components/NewTableCell/NewTableCell';
export { NewTableFootProps } from './components/NewTableFoot/NewTableFoot';
export { NewTableHeadProps } from './components/NewTableHead/NewTableHead';
export { NewTableHeadCellProps } from './components/NewTableHeadCell/NewTableHeadCell';
export { NewTableOrderableBodyProps } from './components/NewTableOrderableBody/NewTableOrderableBody';
export { NewTableOrderableRowProps } from './components/NewTableOrderableRow/NewTableOrderableRow';
export { NewTablePinProps } from './components/NewTablePin/NewTablePin';
export { NewTableRibbonProps } from './components/NewTableRibbon/NewTableRibbon';
export { NewTableRowProps } from './components/NewTableRow/NewTableRow';
export { NewTableStripeProps } from './components/NewTableStripe/NewTableStripe';
export { NewTableTwiddleProps } from './components/NewTableTwiddle/NewTableTwiddle';
export { NewTableVirtualWrapperProps } from './components/NewTableVirtualWrapper/NewTableVirtualWrapper';

NewTable.defaultProps = {
  height: undefined,
  density: 'normal',
};

NewTable.displayName = 'NewTable';

export default NewTable;
